import {
  CalendarDateTime,
  getLocalTimeZone,
  toZoned,
} from "@internationalized/date";
import {
  DeleteOutline18Icon,
  EditOutline18Icon,
} from "@vygruppen/spor-icon-react";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import {
  getDaysOfWeek,
  Weekday,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/DayChoice";
import { WeekRepeat } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/PeriodicDuration";
import { Rule } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import { TFunction } from "i18next";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Box, HStack, IconButton, Stack, Text } from "shared/components/Spor";
import { useTheme } from "styled-components";

const getAllDayNames = (days: Weekday[]) =>
  days
    .map((day, i, self) => {
      if (i === 0) return `${day.name}`;
      if (self.length === i + 1) return ` og ${day.name}`;
      return `, ${day.name}`;
    })
    .join("");

const getGroupedDayNames = (days: Weekday[]) => {
  const startDay = days[0].name;
  const endDay = days[days.length - 1].name;
  return `${startDay} - ${endDay}`;
};

const getFormattedDays = (
  dayNumbers: number[],
  repeat: WeekRepeat,
  translate: TFunction<"translation", undefined>,
) => {
  const days = getDaysOfWeek(translate)
    .sort((a, b) => a.dayNumber - b.dayNumber)
    .filter((day) => dayNumbers.includes(day.dayNumber));

  const isConsecutiveDays = days.every((day, i, self) => {
    const isLastElement = i === self.length - 1;

    return isLastElement || self[i + 1].dayNumber === day.dayNumber + 1;
  });

  const dayNames =
    days.length > 1 && isConsecutiveDays
      ? getGroupedDayNames(days)
      : getAllDayNames(days);

  return `${days.length === 7 ? translate("alle dager") : dayNames} ${
    repeat === WeekRepeat.WEEKLY ? translate("hver") : translate("annenhver")
  } ${translate("uke")}`;
};

const formatDate = (date: CalendarDateTime) =>
  `${format(toZoned(date, getLocalTimeZone()).toDate(), "dd.MM.yyyy", { locale: nb })}`;

const formatTime = (time: CalendarDateTime) =>
  `${format(toZoned(time, getLocalTimeZone()).toDate(), "HH:mm", { locale: nb })}`;

type TimeIntervalSummaryProps = {
  rule: Rule;
  onEditClick: () => void;
  onDeleteRule: () => void;
};

export const TimeIntervalSummary: FC<TimeIntervalSummaryProps> = ({
  rule,
  onEditClick,
  onDeleteRule,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <Box
      padding="24px"
      boxShadow={`inset 0 0 0 1px ${theme.colorOutlineMain}`}
      borderRadius="sm"
    >
      <HStack justifyContent="space-between">
        <Stack>
          <Text variant="xs" fontWeight="bold">
            {`${formatDate(rule.startDateTime)} - ${formatDate(rule.endDateTime)}`}
          </Text>
          <Text variant="xs">
            {t("Mellom kl {{startTime}} og {{endTime}}, {{days}}", {
              startTime: formatTime(rule.startDateTime),
              endTime: formatTime(rule.endDateTime),
              days: getFormattedDays(rule.days, rule.repeat, t),
            })}
          </Text>
        </Stack>
        <HStack>
          <IconButton
            variant="ghost"
            icon={<EditOutline18Icon />}
            aria-label={t("Rediger regel")}
            onClick={onEditClick}
          />
          <IconButton
            variant="ghost"
            icon={<DeleteOutline18Icon />}
            aria-label={t("Slett regel")}
            onClick={onDeleteRule}
          />
        </HStack>
      </HStack>
    </Box>
  );
};
